// Gegner im Spielmodus: Stockfish (begrenzte Stärke), Maia (spielt wie Menschen ihrer Stärke) und
// Rodent IV mit Persönlichkeiten berühmter Spieler und Stile. Namen, Server-Anfrage, gespeicherte Wahl.
import { t } from './i18n';
import { ELO_BEGIN, ELO_FULL, eloLabel } from './playelo';

export type BotKind = 'stockfish' | 'maia' | 'rodent';

export interface BotChoice {
  kind: BotKind;
  stockfishElo: number;
  maiaLevel: number;
  rodentElo: number;
  personality: string;
}

export const DEFAULT_BOT: BotChoice = { kind: 'stockfish', stockfishElo: 1500, maiaLevel: 1500, rodentElo: 1500, personality: 'tal' };
export const MAIA = { min: 1100, max: 1900, step: 100 } as const;
export const RODENT = { min: 800, max: 2800, step: 50 } as const;

/** Spieler in Rodents Persönlichkeiten; Namen in der Schreibweise der Sprache über t() */
export const PLAYERS: Record<string, string> = {
  alekhine: 'Aljechin', anand: 'Anand', anderssen: 'Anderssen', botvinnik: 'Botwinnik', fischer: 'Fischer', karpov: 'Karpow',
  kasparov: 'Kasparow', kortchnoi: 'Kortschnoi', larsen: 'Larsen', lasker: 'Lasker', marshall: 'Marshall', morphy: 'Morphy',
  nimzowitsch: 'Nimzowitsch', petrosian: 'Petrosjan', reti: 'Réti', rubinstein: 'Rubinstein', spassky: 'Spasski',
  steinitz: 'Steinitz', tal: 'Tal', tarrasch: 'Tarrasch', topalov: 'Topalow',
};

/** Stile ohne Vorbild */
export const STYLES: Record<string, string> = {
  defender: 'Verteidiger', dynamic: 'Dynamiker', pawnsacker: 'Bauernopferer', spitfire: 'Hitzkopf', strangler: 'Würger', swapper: 'Abtauscher',
};

const clamp = (v: unknown, lo: number, hi: number, step: number, dflt: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(lo, Math.min(hi, Math.round(n / step) * step)) : dflt;
};

/** Gespeicherte oder fremde Werte auf erlaubte Stufen bringen */
export function normalize(raw: Partial<Record<keyof BotChoice, unknown>>): BotChoice {
  const kind = ['stockfish', 'maia', 'rodent'].includes(raw.kind as string) ? (raw.kind as BotKind) : DEFAULT_BOT.kind;
  const p = String(raw.personality ?? '');
  return {
    kind,
    stockfishElo: clamp(raw.stockfishElo, ELO_BEGIN, ELO_FULL, 50, DEFAULT_BOT.stockfishElo),
    maiaLevel: clamp(raw.maiaLevel, MAIA.min, MAIA.max, MAIA.step, DEFAULT_BOT.maiaLevel),
    rodentElo: clamp(raw.rodentElo, RODENT.min, RODENT.max, RODENT.step, DEFAULT_BOT.rodentElo),
    personality: p in PLAYERS || p in STYLES ? p : DEFAULT_BOT.personality,
  };
}

export function personalityName(p: string): string {
  return p in PLAYERS ? t('{n}-Stil', { n: t(PLAYERS[p]) }) : t(STYLES[p] ?? p);
}

export function botName(c: BotChoice): string {
  if (c.kind === 'maia') return `Maia · ${c.maiaLevel}`;
  if (c.kind === 'rodent') return `${personalityName(c.personality)} · ${c.rodentElo} Elo`;
  return `Stockfish · ${eloLabel(c.stockfishElo)}`;
}

/** Körper für POST /api/play/move; Stockfish ohne `bot`, wie bisher */
export function botRequest(c: BotChoice): { elo: number; bot?: { kind: BotKind; level: number; personality?: string } } {
  if (c.kind === 'maia') return { elo: c.maiaLevel, bot: { kind: 'maia', level: c.maiaLevel } };
  if (c.kind === 'rodent') return { elo: c.rodentElo, bot: { kind: 'rodent', level: c.rodentElo, personality: c.personality } };
  return { elo: c.stockfishElo };
}
